/**
 * Local-date helpers for the Espresso dashboard.
 *
 * All week math is done against ISO-Monday week starts, formatted as
 * `YYYY-MM-DD` to match the `week_start` strings the API returns. We build dates
 * from local parts to avoid UTC drift shifting a Monday to the previous Sunday.
 */

/** Build a local Date at midnight from a `YYYY-MM-DD` (or ISO) string. */
export function parseLocalDate(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(value);
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/** Format a Date as a local `YYYY-MM-DD` string. */
export function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** The ISO-Monday of the week containing `date`, as a `YYYY-MM-DD` string. */
export function mondayOf(date: Date): string {
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = (local.getDay() + 6) % 7; // 0 = Mon … 6 = Sun
  local.setDate(local.getDate() - diff);
  return toISODate(local);
}

/** Add `days` to a `YYYY-MM-DD` string, returning a new `YYYY-MM-DD` string. */
export function addDays(isoDate: string, days: number): string {
  const d = parseLocalDate(isoDate);
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

/** Short label like "Jul 14". */
export function formatShort(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  return parseLocalDate(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/** Long label like "24 Aug 2026". */
export function formatLong(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  return parseLocalDate(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Whole days from today's local date to `deadline` (negative = overdue). */
export function daysUntil(deadline: string): number {
  const target = parseLocalDate(deadline);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}
